// SRT link quality of one input/leg, as a single tone an operator can read at a
// glance without parsing raw RTT/loss numbers.
// Idea ported from /Users/alexey/projects/Viory/streamogon/frontend/src/components/srt/SRTHealthScore.vue
// (a 0-100 score from rtt/loss/retransmits) - rebuilt here as thresholds against
// the console's Tone scale instead of a bespoke score widget, and against the
// fields domain.SRTStats actually carries.
//
// Moved here from features/channel/srtQuality.ts by C3-summary: the shared
// LegMetrics component (legs of a route) needs the exact same reading an
// input's "RTT / потери" cell already uses, and shared/ must not import from
// features/ - this is the one file both features/channel and features/routes
// (through LegMetrics) need, so it lives at the layer both depend on.

import type { SRTStats } from '@/api/types'
import { sharedUi } from '@/shared/i18n/messages'
import { DETECTED_WARN_PCT, UNRECOVERED_ALARM_PCT } from './srtLoss'
import type { Tone } from './tone'

export interface SrtQuality {
  tone: Tone
  label: string
}

/**
 * RTT/loss bands: kept boring on purpose (see tone.ts) - warn only what needs a
 * look, alarm only what needs action. Loss follows the two SRT alarms
 * (решение 24.09, SRTALARM review): DETECTED loss (loss_pct_10s) means "линк
 * болеет" and never reads worse than warn, however high - retransmission may
 * be recovering all of it; UNRECOVERED loss (lost_pct_10s) above the
 * srt_unrecovered default is a hole on air and reads alarm, any smaller
 * unrecovered loss reads warn. The chip therefore never contradicts the three
 * numbers next to it (srtLoss.ts): both use the SAME two thresholds
 * (DETECTED_WARN_PCT, UNRECOVERED_ALARM_PCT - the srt_loss / srt_unrecovered
 * defaults, "value > threshold" like the tracker). Until w9 the chip warned
 * on detected loss from 1 % while the numbers stayed neutral up to 2 %
 * (SRTALARM review, "пороги подсветки расходятся").
 */
const RTT_WARN_MS = 150
const RTT_ALARM_MS = 400

/** Reads an SRT link's quality from its stats; undefined (no live SRT data) reads as idle. */
export function srtQuality(srt: SRTStats | null | undefined): SrtQuality {
  if (!srt) return { tone: 'idle', label: sharedUi.t('srtNoData') }
  const rtt = srt.rtt_ms
  const detected = srt.loss_pct_10s
  const unrecovered = srt.lost_pct_10s ?? 0
  if (unrecovered > UNRECOVERED_ALARM_PCT || rtt >= RTT_ALARM_MS) return { tone: 'alarm', label: sharedUi.t('srtBad') }
  if (unrecovered > 0 || detected > DETECTED_WARN_PCT || rtt >= RTT_WARN_MS) return { tone: 'warn', label: sharedUi.t('srtUnstable') }
  return { tone: 'ok', label: sharedUi.t('srtOk') }
}

/** Retransmits as a share of total packets sent, 0..100; NaN (no packets yet) reads as 0. */
export function retransmitPct(srt: SRTStats): number {
  if (srt.pkt_total <= 0) return 0
  return (srt.pkt_retrans / srt.pkt_total) * 100
}

/** Drop rate as a share of total packets, 0..100. */
export function dropPct(srt: SRTStats): number {
  if (srt.pkt_total <= 0) return 0
  return (srt.pkt_drop / srt.pkt_total) * 100
}
